/**
 * @file referral.ts
 * Utility functions for generating referral codes and managing referral cookies.
 */

export const REFERRAL_COOKIE_CODE = "hirecarta_ref_code";
export const REFERRAL_COOKIE_NAME = "hirecarta_ref_name";

/**
 * Slugifies string by converting to lowercase and stripping non-alphanumeric characters.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "")
    .substring(0, 15);
}

/**
 * Generates a unique, hybrid referral code using first name, last initial, and a random hex string.
 * Format: [first-name]-[last-initial]-[4-char-suffix]
 */
export function generateReferralCode(firstName: string, lastName?: string): string {
  const cleanFirst = slugify(firstName || "user");
  const cleanLastInitial = lastName ? slugify(lastName.charAt(0)) : "";
  
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let suffix = "";
  for (let i = 0; i < 4; i++) {
    suffix += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  const prefix = cleanLastInitial ? `${cleanFirst}-${cleanLastInitial}` : cleanFirst;
  return `${prefix}-${suffix}`;
}

/**
 * Retrieves a cookie by name client-side.
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop()?.split(";").shift() || "");
  }
  return null;
}

/**
 * Sets a cookie client-side with a given expiration (default 30 days).
 */
export function setCookie(name: string, value: string, days = 30) {
  if (typeof document === "undefined") return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `; expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax; Secure`;
}

/**
 * Clears a cookie client-side.
 */
export function clearCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure`;
}
